import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
  UploadTask,
} from "firebase/storage";
import { firebaseApp } from "../../api/firebase/firestoreService";

interface FileUploadState {
  progress: number;
  downloadURL: string | null;
  error: string | null;
  isUploading: boolean;
}

const initialState: FileUploadState = {
  progress: 0,
  downloadURL: null,
  error: null,
  isUploading: false,
};

// Store upload tasks outside Redux state to avoid Immer freezing issues
// This prevents "Cannot add property 0, object is not extensible" error
const uploadTasksMap = new Map<string, UploadTask>();

const fileUploadSlice = createSlice({
  name: "fileUpload",
  initialState,
  reducers: {
    startUpload(state) {
      state.isUploading = true;
      state.progress = 0;
      state.error = null;
      state.downloadURL = null;
    },
    setProgress(state, action: PayloadAction<number>) {
      state.progress = action.payload;
      state.error = null;
      state.downloadURL = null;
    },
    setDownloadURL(state, action: PayloadAction<string>) {
      state.downloadURL = action.payload;
      state.progress = 100;
      state.isUploading = false;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.progress = 0;
      state.downloadURL = null;
      state.isUploading = false;
    },
    cancelUpload(state, action: PayloadAction<string>) {
      const uploadTask = uploadTasksMap.get(action.payload);
      if (uploadTask) {
        uploadTask.cancel();
        uploadTasksMap.delete(action.payload);
      }
      state.isUploading = false;
      state.progress = 0;
      state.downloadURL = null;
      state.error = null;
    },
    resetUploadState(state) {
      state.progress = 0;
      state.downloadURL = null;
      state.error = null;
      state.isUploading = false;
    },
  },
});

export const {
  setProgress,
  setDownloadURL,
  setError,
  cancelUpload,
  startUpload,
  resetUploadState,
} = fileUploadSlice.actions;

export const uploadFileToFirebaseStorage =
  (file: File, filePath: string) => async (dispatch) => {
    const storage = getStorage(firebaseApp);
    const storageRef = ref(storage, filePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    // Store the upload task outside Redux state to avoid Immer freezing
    uploadTasksMap.set(filePath, uploadTask);
    dispatch(startUpload());

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

        dispatch(setProgress(progress));
      },
      (error) => {
        uploadTasksMap.delete(filePath);
        dispatch(setError(error.message));
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        uploadTasksMap.delete(filePath);
        dispatch(setDownloadURL(downloadURL));
      },
    );
  };

export const cancelFileUpload = (filePath: string) => (dispatch) => {
  dispatch(cancelUpload(filePath));
};

export const resetFileUploadState = () => (dispatch) => {
  dispatch(resetUploadState());
};

export default fileUploadSlice.reducer;
